import {Component, Input, OnInit} from '@angular/core';
import {
    ReferenceBookResponse,
    ReferenceBookType,
    TrendApiService,
    TrendResponse,
    TrendStatisticResponse,
} from '@npck/af-frontend-api';
import {DATE_UI_FORMAT, rxSpinner} from '@npck/libs/commons';
import dayjs from 'dayjs';
import {combineLatest} from 'rxjs';
import {initialTableEvent, mergeTableEvent, TableEvent} from '../../../../../components/full-table/full-table.component';
import {Column, ColumnType, Row} from '../../../../../components/full-table/table/table.component';
import {DictionaryHolderService, findValueInDictionary} from '../../../../../domain/dictionary-holder.service';
import {TrendDetailsFilterHolderService} from '../../../../../domain/trend-details-filter-holder.service';

const TREND_STATISTIC_TAB = 'tabTrendStatistics';

@Component({
    selector: 'af-trend-statistic-tab',
    templateUrl: './trend-statistic-tab.component.html',
    styleUrl: './trend-statistic-tab.component.scss',
    standalone: false,
})
export class TrendStatisticTabComponent implements OnInit {
    @Input() trend: TrendResponse;
    dataSource: Row[] = [];
    tableEvent: TableEvent;
    response: TrendStatisticResponse[];

    constructor(
        private trendApiService: TrendApiService,
        private dictionaryHolderService: DictionaryHolderService,
        private filterHolderService: TrendDetailsFilterHolderService,
    ) {
    }

    ngOnInit(): void {
        this.loadStatistics(
            this.filterHolderService.getState(this.trend.id, TREND_STATISTIC_TAB)?.tableEvent
                ?? this.buildInitialTableEvent(),
        );
    }

    loadStatistics(tableEvent: TableEvent): void {
        const prevEvent = this.filterHolderService.getState(this.trend.id, TREND_STATISTIC_TAB)?.tableEvent;
        this.tableEvent = mergeTableEvent(prevEvent, tableEvent);
        this.filterHolderService.storeTableState(this.trend.id, TREND_STATISTIC_TAB, this.tableEvent);
        combineLatest([
            this.dictionaryHolderService.getReference(ReferenceBookType.COUNTRY),
            this.dictionaryHolderService.getReference(ReferenceBookType.REGION),
            this.trendApiService.getTrendStatistics(this.trend.id, {
                sortOrder: this.tableEvent.sortBy?.direction,
            }),
        ])
            .pipe(rxSpinner(this.tableEvent.spinnerId))
            .subscribe(([countries, regions, response]) => {
                this.buildDataSource(response, countries, regions);
            });
    }

    private buildInitialTableEvent(): TableEvent {
        return {
            ...initialTableEvent,
            spinnerId: 'trendStatistics',
            filter: {},
            sortBy: {
                columnName: 'periodDetected',
                direction: 'desc',
            },
        };
    }

    private buildDataSource(
        response: TrendStatisticResponse[],
        countries: ReferenceBookResponse[],
        regions: ReferenceBookResponse[],
    ): void {
        this.dataSource = response.map(item => {
            return <Row>{
                country: <Column>{
                    type: ColumnType.TEXT,
                    text: findValueInDictionary(countries, item.country, 'name_ru') ?? '-',
                },
                region: <Column>{
                    type: ColumnType.TEXT,
                    text: findValueInDictionary(regions, item.region, 'name_ru') ?? '-',
                },
                incidentCount: <Column>{
                    type: ColumnType.DIGITS,
                    text: item.incidentCount + '',
                },
                uniqueVictims: <Column>{
                    type: ColumnType.DIGITS,
                    text: item.uniqueVictimCount + '',
                },
                periodDetected: <Column>{
                    type: ColumnType.TEXT,
                    sortable: true,
                    text: [item.periodDetectedFrom, item.periodDetectedTo].filter(n => !!n)
                        .map(n => dayjs(n).format(DATE_UI_FORMAT)).join(' - '),
                },
            };
        });
    }
}
